import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Trash2 } from 'lucide-react';
import {
  UniversalPage,
  UniversalTable,
  type UniversalTableColumn,
  Card,
  NotificationCard,
  StatusBadge,
  PeriodFilter,
  SearchField,
  TABLE_PRESETS,
} from '@afrilink/ui';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import {
  adminAccountsService,
  type AdminAccountRow,
} from '@/domains/hosting/services/adminAccountsService';
import { adminSettingsService } from '@/domains/settings/services/adminSettingsService';
import { canBeAutoTerminated, dunningPolicyMeta } from '@/domains/hosting/dunningPolicy';
import { formatDateTime } from '@/shared/utils/format';

function terminationCountdown(iso: string | null): string {
  if (!iso) return '—';
  const ms = new Date(iso).getTime() - Date.now();
  const days = Math.round(ms / 86_400_000);
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return 'today';
  return `in ${days}d`;
}

/**
 * Is automated termination actually armed? (F-13)
 *
 * Two independent controls have to BOTH allow it, and neither was visible:
 *
 *   1. `system_setting.removeacct_max_per_day` — the circuit breaker in the
 *      `provisioning_job_removeacct_guard` trigger. At 0, no removal can be
 *      enqueued by any producer.
 *   2. `customer_link.dunning_policy` — billing-cron STEP 4 skips `no_suspend`
 *      and `no_cancel`, and the same trigger refuses them independently.
 *
 * Read live. A sentence in the page copy claiming "termination is disabled"
 * would be correct today and silently wrong the moment someone changes a
 * setting — which is the shape of every stale-documentation defect in this
 * project. If the query fails the banner says so rather than implying safety;
 * "we could not check" must never render as "you are protected".
 */
function ArmingBanner() {
  const [breaker, setBreaker] = useState<number | null>(null);
  const [exposed, setExposed] = useState<number | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      adminSettingsService.listSystemSettings(),
      adminAccountsService.listAll(false),
    ])
      .then(([settings, links]) => {
        if (cancelled) return;
        const raw = settings.find((s) => s.key === 'removeacct_max_per_day')?.value;
        setBreaker(typeof raw === 'number' ? raw : Number(raw ?? NaN));
        setExposed(links.filter((l) => canBeAutoTerminated(l.dunning_policy)).length);
        setTotal(links.length);
      })
      .catch((e: unknown) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (err) {
    return (
      <NotificationCard
        tone="warning"
        title="Could not check whether automated termination is armed"
        body={`Treat this page as inconclusive rather than safe — ${err}`}
      />
    );
  }
  if (breaker === null || exposed === null || total === null) {
    return (
      <Card interactive={false} className="p-4 text-sm text-muted-foreground">
        Checking whether automated termination is armed…
      </Card>
    );
  }

  const breakerOff = Number.isFinite(breaker) && breaker <= 0;
  const disarmed = breakerOff || exposed === 0;

  return (
    <NotificationCard
      tone={disarmed ? 'info' : 'warning'}
      title={
        disarmed
          ? 'Automated termination is currently OFF'
          : `Automated termination is ARMED for ${exposed} of ${total} accounts`
      }
      body={
        <span className="flex flex-col gap-1">
          <span>
            Circuit breaker <code>removeacct_max_per_day</code> ={' '}
            <strong>{Number.isFinite(breaker) ? breaker : 'unreadable'}</strong>
            {breakerOff
              ? ' — at zero, no removal can be enqueued by any producer.'
              : ' — removals are permitted up to this many per day.'}
          </span>
          <span>
            <strong>{exposed}</strong> of {total} live accounts are on a{' '}
            <code>standard</code> dunning policy, which is the only setting under
            which an account can be terminated automatically.
          </span>
          {disarmed && (
            <span className="text-muted-foreground">
              An empty queue below therefore means the mechanism cannot fire — not
              that it ran and found nothing.
            </span>
          )}
        </span>
      }
    />
  );
}

export default function AdminTerminationsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<AdminAccountRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    adminAccountsService
      .listAll(true)
      .then((data) => {
        if (!cancelled) {
          setRows(data.filter((r) => r.scheduled_termination_at !== null));
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * The date here is `Scheduled` — when the account is due to be DESTROYED, a
   * future date, not a past event. That makes the closed presets read oddly
   * ("last month" on a termination queue), so this page offers search plus the
   * full preset list and leaves the soonest-first sort as the primary ordering.
   *
   * Sorting stays outside the hook: soonest-first is not a filter, and an
   * account 3 days from `removeacct` must stay at the top whatever is typed.
   */
  const table = useTableFilters<AdminAccountRow>({
    rows,
    dateMode: 'client',
    dateLabel: 'Scheduled',
    dateOf: (r) => r.scheduled_termination_at,
    searchText: (r) =>
      [
        r.cpanel_username,
        r.primary_domain ?? '',
        r.customer_name ?? '',
        r.customer_email ?? '',
      ].join(' '),
  });

  const sorted = useMemo(
    () =>
      [...table.rows].sort(
        (a, b) =>
          new Date(a.scheduled_termination_at ?? '').getTime() -
          new Date(b.scheduled_termination_at ?? '').getTime(),
      ),
    [table.rows],
  );

  const columns = useMemo<UniversalTableColumn<AdminAccountRow>[]>(
    () => [
      {
        key: 'username',
        header: 'cPanel user',
        sortValue: (r) => r.cpanel_username,
        render: (r) => (
          <div className="flex flex-col">
            <code className="text-sm text-foreground">{r.cpanel_username}</code>
            {r.primary_domain && (
              <span className="text-xs text-muted-foreground">{r.primary_domain}</span>
            )}
          </div>
        ),
      },
      {
        key: 'customer',
        header: 'Customer',
        sortValue: (r) => r.customer_name ?? '',
        render: (r) => (
          <span className="text-foreground">{r.customer_name ?? '—'}</span>
        ),
      },
      {
        key: 'scheduled',
        header: 'Scheduled for',
        sortValue: (r) => new Date(r.scheduled_termination_at ?? '').getTime(),
        render: (r) => (
          <div className="flex flex-col">
            <span className="text-foreground">{formatDateTime(r.scheduled_termination_at)}</span>
            <span className="text-xs text-muted-foreground">
              {terminationCountdown(r.scheduled_termination_at)}
            </span>
          </div>
        ),
      },
      {
        // A row scheduled for termination whose policy forbids it is not a
        // contradiction to hide — it is the most important thing on the line.
        // The date says "this will happen"; the policy says whether it can.
        key: 'dunning',
        header: 'Dunning',
        sortValue: (r) => r.dunning_policy ?? '',
        render: (r) => {
          const meta = dunningPolicyMeta(r.dunning_policy);
          return (
            <span title={meta.detail}>
              <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>
            </span>
          );
        },
        width: 'w-40',
      },
      {
        key: 'status',
        header: 'Status',
        render: (r) => {
          if (r.unlinked_at) return <StatusBadge tone="draft">Terminated</StatusBadge>;
          if (!canBeAutoTerminated(r.dunning_policy)) {
            return <StatusBadge tone="info">Blocked by policy</StatusBadge>;
          }
          const ms = new Date(r.scheduled_termination_at ?? '').getTime() - Date.now();
          if (ms < 0) return <StatusBadge tone="overdue">Overdue</StatusBadge>;
          return <StatusBadge tone="pending">Scheduled</StatusBadge>;
        },
        width: 'w-36',
      },
    ],
    [],
  );

  return (
    <UniversalPage
      title="Termination queue"
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Hosting', href: '/admin/hosting' },
        { label: 'Termination queue' },
      ]}
    >
      {/* F-13. This page used to describe the 60-day auto-cancel rule as
          though it were live, and render "Nothing scheduled for termination"
          as reassurance. Both are true and neither is the useful fact: the
          mechanism is DISARMED TWICE OVER, and nothing on the page said so.
          An empty queue reads as "all clear" when it actually means "this
          cannot fire" — and the difference matters enormously if anyone ever
          re-arms it. The state is read live rather than written into copy,
          because a hardcoded claim about a kill switch goes stale silently. */}
      <ArmingBanner />

      <Card interactive={false} className="border-info/30 bg-info/5 p-4 text-sm">
        <p>
          Accounts with <code>customer_link.scheduled_termination_at</code> set. The
          <code>whm.scheduled_terminate</code> step in the provisioning runner fires at the
          scheduled time and calls WHM <code>removeacct</code>. To cancel a scheduled termination,
          open the customer's Hosting tab and revert the action.
        </p>
      </Card>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <PeriodFilter
          value={table.filters.period}
          from={table.filters.from}
          to={table.filters.to}
          onChange={table.filters.setPeriod}
          onStep={table.stepPeriodBy}
          presets={TABLE_PRESETS}
          variant="menu"
          steppable
          label="Scheduled"
        />
        <SearchField
          value={table.q}
          onValueChange={table.setQ}
          pending={table.isSearching}
          placeholder="Search cPanel user, domain, customer"
          className="ml-auto w-full max-w-xs"
        />
      </div>

      <UniversalTable<AdminAccountRow>
        data={sorted}
        caption={loading ? undefined : table.caption}
        columns={columns}
        getRowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/admin/customers/${r.customer_id}/hosting`)}
        isLoading={loading}
        error={error}
        empty={{
          icon: Trash2,
          title: 'Nothing scheduled for termination',
          description:
            'Scheduled terminations come from cancellation flows or the 60-day suspension auto-cancel rule.',
        }}
      />
    </UniversalPage>
  );
}
